import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Building2, User, Mail, Phone, Globe, Loader2 } from 'lucide-react';
import { IngestProgressCard } from '../../orbit/dossier/IngestProgressCard';
import { createClient, setCompanyInternal, getInternalCompany } from '../../services/api';

/**
 * Create-client form. Replaces the old agentic onboarding chat
 * (/consultant/onboarding). A consultant fills a short form to spin up a
 * client record; discovery happens later inside Orbit intake — shared as a
 * link or run over a scheduled call from the client's detail page.
 */

type FieldKey = 'name' | 'contact_name' | 'email' | 'phone' | 'website';

/** Display host for the read card — falls back to the raw text if unparseable. */
const websiteHost = (raw: string): string => {
    const v = raw.trim();
    try {
        return new URL(v.startsWith('http') ? v : `https://${v}`).host || v;
    } catch {
        return v;
    }
};

// Module-level so it isn't re-created each render (which would remount the
// input and drop focus on every keystroke).
const FormField: React.FC<{
    label: string;
    Icon: any;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    type?: string;
    placeholder?: string;
    optional?: boolean;
    hint?: string;
    autoFocus?: boolean;
}> = ({ label, Icon, value, onChange, type = 'text', placeholder, optional, hint, autoFocus }) => (
    <div>
        <label className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {optional && (
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">Optional</span>
            )}
        </label>
        <div className="relative">
            <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50/60 text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors outline-none"
            />
        </div>
        {hint && <p className="text-xs text-gray-400 mt-1.5">{hint}</p>}
    </div>
);

const NewClient: React.FC = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [featureInviteContext, setFeatureInviteContext] = useState(false);
    const [industry, setIndustry] = useState('');
    const [skipWelcomeEmail, setSkipWelcomeEmail] = useState(false);
    // Super-only, one-time: if no "our company" is set yet, offer to mark the
    // just-created client as US — which makes it a PROVIDER and spins up its
    // tenant on the builder. Skipped once an internal company already exists.
    const isSuper = localStorage.getItem('user_role') === 'super';
    const [hasInternal, setHasInternal] = useState<boolean | null>(null);
    const [prompt, setPrompt] = useState<{ id: string; name: string } | null>(null);
    const [marking, setMarking] = useState(false);
    useEffect(() => {
        import('../../services/agentsApi').then(({ agentsApi }) => {
            agentsApi.getWorkspaceFeatures()
                .then((r: any) => setFeatureInviteContext(Boolean(r.data?.invite_context)))
                .catch(() => setFeatureInviteContext(false));
        });
    }, []);
    useEffect(() => {
        if (!isSuper) return;
        getInternalCompany()
            .then((r: any) => setHasInternal(!!r?.internal))
            .catch(() => setHasInternal(false));
    }, [isSuper]);
    const [form, setForm] = useState<Record<FieldKey, string>>({
        name: '',
        contact_name: '',
        email: '',
        phone: '',
        website: '',
    });

    const set = (key: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [key]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error('Business name is required');
        if (!form.email.trim()) return toast.error('Email is required');
        setSubmitting(true);
        try {
            const res = await createClient({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || undefined,
                website: form.website.trim() || undefined,
                contact_name: form.contact_name.trim() || undefined,
                send_email: !skipWelcomeEmail,
                ...(featureInviteContext && industry.trim()
                    ? { context: { industry: industry.trim() } }
                    : {}),
            });
            toast.success('Client created');
            // One-time super prompt: offer to mark this as "us" (provider).
            if (isSuper && hasInternal === false) {
                setPrompt({ id: res.client_id, name: form.name.trim() });
            } else {
                navigate(`/consultant/clients/${res.client_id}`);
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to create client');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto px-4 py-10">
            <button
                onClick={() => navigate('/consultant/dashboard')}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                Clients
            </button>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Header band */}
                <div className="px-8 pt-8 pb-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600">
                            <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 leading-tight">New client</h1>
                            <p className="text-sm text-gray-500">Create the account — discovery happens in Orbit afterward.</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">
                    <FormField
                        label="Business name" Icon={Building2}
                        value={form.name} onChange={set('name')}
                        placeholder="Acme Inc." autoFocus
                    />
                    <FormField
                        label="Primary contact" Icon={User}
                        value={form.contact_name} onChange={set('contact_name')}
                        placeholder="Jane Doe" optional
                    />
                    <FormField
                        label="Email" Icon={Mail} type="email"
                        value={form.email} onChange={set('email')}
                        placeholder="jane@acme.com"
                        hint="A placeholder email is fine — the client uses the Orbit link, not a login."
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <FormField
                            label="Phone" Icon={Phone} type="tel"
                            value={form.phone} onChange={set('phone')}
                            placeholder="+1 555 123 4567" optional
                            hint="Needed only to place a discovery call."
                        />
                        <FormField
                            label="Website" Icon={Globe}
                            value={form.website} onChange={set('website')}
                            placeholder="acme.com" optional
                        />
                    </div>

                    {featureInviteContext && (
                        <div>
                            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Industry (optional context)</label>
                            <input
                                type="text"
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                placeholder="e.g. fintech, healthcare, B2B SaaS"
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-400"
                            />
                            <p className="text-xs text-gray-400 mt-1">Prefills the company dossier so Map the Business confirms it instead of asking cold.</p>
                        </div>
                    )}

                    <label className="flex items-start gap-2.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={skipWelcomeEmail}
                            onChange={(e) => setSkipWelcomeEmail(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span className="text-sm text-gray-700">
                            Don't send a welcome email
                            <span className="block text-xs text-gray-400 mt-0.5">
                                Skip the credentials email — useful when the address is a placeholder.
                            </span>
                        </span>
                    </label>

                    {/* A2 — while the create request is in flight AND a website was
                        given, say what is actually happening. Creating the client
                        starts a background read of that site (client.py kicks
                        process_website_scraping), so the consultant used to get a
                        bare "Creating…" spinner for work that was really a site
                        read. Same card as Orbit's landing read, so both entry
                        points describe the read the same way. */}
                    {submitting && form.website.trim() && (
                        <div className="pt-1">
                            <IngestProgressCard host={websiteHost(form.website)} kind="url" />
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => navigate('/consultant/dashboard')}
                            className="px-4 py-2.5 text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-sm transition-colors disabled:opacity-60 whitespace-nowrap"
                            disabled={submitting}
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            {submitting ? 'Creating…' : 'Create client'}
                        </button>
                    </div>
                </form>
            </div>

            {/* One-time super prompt: mark the new client as US → provider → tenant. */}
            {prompt && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
                        <h2 className="text-base font-semibold text-gray-900">Is “{prompt.name}” your own company?</h2>
                        <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                            Marking it as <span className="font-medium text-gray-700">us</span> makes it a{' '}
                            <span className="font-medium text-gray-700">provider</span> — you'll manage your own experts
                            and reach the builder from it. You can only set this once.
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => { const id = prompt.id; setPrompt(null); navigate(`/consultant/clients/${id}`); }}
                                disabled={marking}
                                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                            >
                                No, it's a client
                            </button>
                            <button
                                onClick={async () => {
                                    setMarking(true);
                                    try {
                                        await setCompanyInternal(prompt.id, true);
                                        toast.success(`${prompt.name} is now your company (provider)`);
                                    } catch (e: any) {
                                        toast.error(e?.response?.data?.detail || 'Could not mark as provider');
                                    } finally {
                                        const id = prompt.id;
                                        setMarking(false);
                                        setPrompt(null);
                                        navigate(`/consultant/clients/${id}`);
                                    }
                                }}
                                disabled={marking}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap"
                            >
                                Yes, this is us
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewClient;
